import { useIntl } from 'react-intl';
import { useRestoreMemberMutation } from 'src/api/mutations/userManagementMutation';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';

import styles from '../styles.module.scss';
import messages from '../messages';

interface RestoreMemberButtonProps {
  userId: number;
  email: string;
  /** False when the plan has no free seat of this member's class. */
  seatAvailable: boolean;
  onRestored: () => void;
}

/**
 * Undo a removal — plan-structure v2 Phase 7a. Restoring consumes a seat, so
 * the server runs the same gate as an invite; we disable the button when we
 * already know there is no room (with a tooltip saying why) and still handle
 * the `plan_upgrade_required` 403 for the race where someone else took the
 * last seat first.
 */
const RestoreMemberButton = ({ userId, email, seatAvailable, onRestored }: RestoreMemberButtonProps) => {
  const { formatMessage } = useIntl();
  const { showSuccess, showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();

  const restoreMutation = useRestoreMemberMutation(userId, {
    onSuccess: () => {
      showSuccess(formatMessage(messages.restoreMemberSuccess, { email }));
      onRestored();
    },
    onError: (error: any) => {
      const gate = planUpgradeRequired(error);
      showAlert(gate ? (gate.message ?? formatMessage(messages.noSeatToRestore)) : getErrorMessage(error));
    },
  });

  return (
    <button
      className={styles.resendButton}
      onClick={() => restoreMutation.mutate()}
      disabled={restoreMutation.isLoading || !seatAvailable}
      title={seatAvailable ? undefined : formatMessage(messages.noSeatToRestore)}
    >
      {restoreMutation.isLoading ? '...' : formatMessage(messages.restoreMember)}
    </button>
  );
};

export default RestoreMemberButton;
